import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Form,
  Modal,
  Offcanvas,
  Pagination,
  Table
} from "react-bootstrap";
import { useOutletContext } from "react-router-dom";
import NoData from "../Components/NoData";
import Shimmer from "../Components/Shimmer";
import {
  getOperationCapabilities,
  getOperationDrawerTabs,
  getPublicOperationReference,
  OPERATION_STATUS_FILTER_OPTIONS
} from "../features/installmentOperations/operationPresentation";
import { hasActiveOperationFilters } from "../features/installmentOperations/operationFilters";
import ContractPdfWorkspace from "../features/installmentOperations/ContractPdfWorkspace";
import ContractReviewDecisionPanel from "../features/installmentOperations/ContractReviewDecisionPanel";
import UserManagementHeader from "../features/userManagement/UserManagementHeader";
import UserManagementPagination from "../features/userManagement/UserManagementPagination";
import AdminInstallmentOperationsHook from "../hooks/AdminInstallmentOperationsHook";
import "../css/UserManagement.css";
import "../css/AdminInstallmentOperations.css";

const STATUS_LABELS = {
  en: {
    REQUEST_SUBMITTED: "New request and under merchant review",
    MERCHANT_REVIEWING: "New request and under merchant review",
    MERCHANT_OFFER_CREATED: "Merchant offer ready",
    CUSTOMER_OFFER_ACCEPTED: "Customer accepted merchant offer",
    CONTRACT_GENERATED: "Contract generated after branch handover",
    WAITING_FOR_SIGNATURE: "Waiting for signature",
    SIGNED_CONTRACT_UPLOADED:
      "Signed contract uploaded and waiting for responsible officer review",
    WAITING_FOR_PLATFORM_FEE_PAYMENT: "Waiting for platform fee payment",
    WAITING_FOR_PLATFORM_ADMIN_CONTRACT_REVIEW:
      "Signed contract uploaded and waiting for responsible officer review",
    CONTRACT_REJECTED: "Contract rejected",
    REUPLOAD_REQUIRED: "Re-upload required",
    CONTRACT_APPROVED: "Contract approved",
    ACTIVE_INSTALLMENT_OPERATION: "Active installment operation",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
    IN_DISPUTE: "In dispute",
    NOT_GENERATED: "Not generated",
    GENERATED: "Generated",
    SIGNED_UPLOADED: "Signed uploaded",
    PENDING_ADMIN_REVIEW: "Pending platform admin review",
    CONTRACT_UNDER_REVIEW: "Contract under review",
    UNDER_REVIEW: "Contract under review",
    APPROVED: "Approved",
    REJECTED: "Rejected",
    NOT_SUBMITTED: "Not submitted",
    PENDING_PAYMENT: "Pending platform fee payment",
    PAYMENT_REQUESTED: "Platform fee payment requested",
    PAID: "Paid",
    MANUALLY_CONFIRMED: "Manually confirmed",
    FAILED: "Failed",
    PENDING: "Pending",
    RECORDED: "Recorded",
    NOT_REQUIRED: "Not required",
    REFUNDED: "Refunded",
    NONE: "No dispute",
    REQUEST_REVIEW: "Request review",
    PRE_SIGNATURE: "Before signed upload"
  },
  ar: {
    REQUEST_SUBMITTED: "طلب جديد وتحت مراجعة التاجر",
    MERCHANT_REVIEWING: "طلب جديد وتحت مراجعة التاجر",
    MERCHANT_OFFER_CREATED: "عرض التاجر جاهز",
    CUSTOMER_OFFER_ACCEPTED: "قبل العميل عرض التاجر",
    CONTRACT_GENERATED: "تم إنشاء العقد بعد تأكيد الاستلام بالفرع",
    WAITING_FOR_SIGNATURE: "في انتظار التوقيع",
    SIGNED_CONTRACT_UPLOADED:
      "تم رفع العقد الموقع وفي انتظار مراجعة المسؤول",
    WAITING_FOR_PLATFORM_FEE_PAYMENT: "في انتظار سداد رسوم المنصة",
    WAITING_FOR_PLATFORM_ADMIN_CONTRACT_REVIEW:
      "تم رفع العقد الموقع وفي انتظار مراجعة المسؤول",
    CONTRACT_REJECTED: "العقد مرفوض",
    REUPLOAD_REQUIRED: "مطلوب إعادة رفع العقد",
    CONTRACT_APPROVED: "العقد معتمد",
    ACTIVE_INSTALLMENT_OPERATION: "عملية تقسيط نشطة",
    COMPLETED: "مكتملة",
    CANCELLED: "ملغاة",
    IN_DISPUTE: "محل نزاع",
    NOT_GENERATED: "لم يتم إنشاء العقد",
    GENERATED: "تم إنشاء العقد",
    SIGNED_UPLOADED: "تم رفع العقد الموقع",
    PENDING_ADMIN_REVIEW: "في انتظار مراجعة مسؤول المنصة",
    CONTRACT_UNDER_REVIEW: "العقد قيد المراجعة",
    UNDER_REVIEW: "العقد قيد المراجعة",
    APPROVED: "معتمد",
    REJECTED: "مرفوض",
    NOT_SUBMITTED: "لم يتم الرفع",
    PENDING_PAYMENT: "في انتظار سداد رسوم المنصة",
    PAYMENT_REQUESTED: "تم طلب سداد رسوم المنصة",
    PAID: "تم السداد",
    MANUALLY_CONFIRMED: "تم التأكيد يدويا",
    FAILED: "تعذر السداد",
    PENDING: "قيد الانتظار",
    RECORDED: "تم التسجيل",
    NOT_REQUIRED: "غير مطلوبة",
    REFUNDED: "تم رد الرسوم",
    NONE: "لا يوجد نزاع",
    REQUEST_REVIEW: "مراجعة الطلب",
    PRE_SIGNATURE: "قبل رفع العقد الموقع"
  }
};

const PAGE_TEXT = {
  en: {
    nav: "Installment Requests",
    all: "All",
    complete: "Complete",
    incomplete: "Incomplete",
    yes: "Yes",
    no: "No",
    notPresent: "Not present",
    secureAvailable: "Available through a secure endpoint",
    unavailable: "Unavailable",
    eyebrow: "Operations workspace",
    title: "Installment Requests",
    subtitle: "Manage and follow up installment requests, contracts, and platform fees",
    total: "Total",
    refresh: "Refresh",
    rowsPerPage: "Rows per page",
    page: "Page",
    previous: "Previous",
    next: "Next",
    searchPlaceholder: "Search operations...",
    summary: {
      total: "Total operations",
      waitingReview: "Waiting contract review",
      reuploadRequired: "Re-upload required",
      contractApproved: "Approved contracts",
      waitingPlatformFee: "Waiting platform fee",
      active: "Active operations",
      inDispute: "In dispute"
    },
    filters: {
      operationReference: "Operation reference",
      operationStatus: "Operation status",
      contractReviewStatus: "Contract review",
      platformFeeStatus: "Platform fee",
      operationDate: "Operation date",
      allDates: "All dates",
      today: "Today",
      last7Days: "Last 7 days",
      last30Days: "Last 30 days",
      last90Days: "Last 90 days",
      customRange: "Custom range",
      customOperationRange: "Custom operation date range",
      fromDate: "From date",
      toDate: "To date",
      editCustomRange: "Edit custom date range",
      apply: "Apply",
      cancel: "Cancel",
      showFilters: "Show filters",
      hideFilters: "Hide filters",
      reset: "Clear filters"
    },
    columns: {
      operationReference: "Operation reference",
      customer: "Customer",
      merchant: "Merchant",
      product: "Product/service",
      cashPrice: "Cash price",
      finalDeferredSalePrice: "Final deferred sale price",
      platformFee: "Platform fee",
      operationStatus: "Operation status",
      contract: "Contract",
      dispute: "Dispute",
      merchantRejection: "Merchant rejection",
      contractSerial: "Contract serial",
      updatedAt: "Last update",
      action: "Action"
    },
    buttons: {
      viewDetails: "View details",
      viewGenerated: "View original contract",
      downloadGenerated: "Download original contract",
      viewSigned: "View signed contract",
      downloadSigned: "Download signed contract",
      viewSignedFile: "View signed file",
      downloadSignedFile: "Download signed file",
      approveContract: "Approve contract",
      rejectContract: "Reject contract",
      confirmPlatformFee: "Confirm platform fee"
    },
    detailTitle: "Operation Details",
    drawer: {
      eyebrow: "Operation workflow",
      currentStage: "Current stage",
      chooseReviewDecision: "Review decision",
      approve: "Approve",
      reject: "Reject",
      saveReview: "Save review decision",
      reviewComplete: "This contract review is read-only in the current state.",
      feeComplete: "The platform fee is complete. Manual confirmation inputs are hidden.",
      feeUnavailable: "Manual merchant-to-platform remittance confirmation is not available in the current state.",
      optionalDetails: "Optional confirmation details",
      noActionRequired: "No administrator input is required at this stage.",
      automaticActivationWaiting:
        "The operation will activate automatically when contract approval and platform-fee completion are both verified.",
      automaticActivationInProgress:
        "All activation requirements are complete. The backend is activating the operation automatically.",
      automaticActivationComplete:
        "The operation was activated automatically and active status follow-up is available."
    },
    tabs: {
      overview: "Overview",
      files: "Contract files",
      review: "Contract review",
      fee: "Merchant-to-platform remittance",
      activation: "Activation",
      events: "Events",
      dispute: "Dispute"
    },
    section: {
      statusDimensions: "Status dimensions",
      partiesProduct: "Parties and product",
      pricesSchedule: "Prices and planned schedule",
      customerReceipts: "Customer-to-merchant initial payment",
      activationReadiness: "Automatic activation requirements",
      merchantRejection: "Merchant rejection / cancellation",
      contractFiles: "Contract files",
      signedContract: "Signed contract",
      uploadAttempts: "Upload attempts history",
      reviewChecklist: "Contract review checklist",
      platformFee: "Merchant-to-platform remittance",
      manualConfirmation: "Manual remittance confirmation",
      activationGates: "Activation gates",
      events: "Event log",
      dispute: "Dispute"
    },
    detail: {
      operationReference: "Operation reference",
      customer: "Customer",
      customerPhone: "Customer phone",
      nationalId: "National ID",
      merchant: "Merchant",
      merchantPhone: "Merchant phone",
      product: "Product/service",
      cashPrice: "Cash price",
      finalDeferredSalePrice: "Final deferred sale price",
      downPayment: "Down payment",
      downPaymentReceipt: "Down payment receipt",
      customerPlatformFeeReceipt: "Platform fee receipt",
      receiptStatus: "Receipt status",
      recordedAt: "Recorded at",
      receiptReference: "Receipt reference",
      receiptNote: "Receipt note",
      platformFeePercentage: "Platform fee percentage",
      platformFee: "Platform fee",
      installmentCount: "Installment count",
      installmentAmount: "Installment amount",
      signedUpload: "Signed contract upload",
      adminApproval: "Platform admin approval",
      generatedFile: "Original contract file",
      canActivate: "Activation eligibility",
      contractSerial: "Contract serial",
      contractStatus: "Contract status",
      contractReview: "Contract review",
      attemptNumber: "Attempt number",
      uploadStatus: "Upload status",
      uploadedAt: "Uploaded at",
      uploadAttemptsCount: "Upload attempts count",
      uploader: "Uploader",
      file: "File",
      rejectionReason: "Rejection reason",
      rejectionStage: "Rejection stage",
      rejectedBy: "Rejected by",
      rejectedAt: "Rejected at",
      visibleToCustomer: "Visible to customer",
      visibleToAdmin: "Visible to admin",
      latestAttempt: "Latest attempt",
      action: "Action",
      reviewReason: "Rejection reason",
      reviewNotes: "Review notes",
      feeAmount: "Fee amount",
      percentage: "Percentage",
      baseType: "Base type",
      baseAmount: "Base amount",
      platformFeeStatus: "Platform fee status",
      paymentProvider: "Payment provider",
      paymentReference: "Payment reference",
      manualConfirmation: "Manual confirmation",
      paymentMethod: "Payment method",
      note: "Note",
      confirmationDate: "Confirmation date",
      amount: "Amount",
      currency: "Currency",
      activeTrackingGate: "Active status follow-up",
      missingGates: "Incomplete gates:",
      event: "Event",
      role: "Role",
      date: "Date",
      safeSummary: "Safe summary",
      disputeStatus: "Dispute status",
      hasDispute: "Has dispute"
    },
    checklist: {
      serialMatchesOperation: "Contract serial matches operation",
      sameMerchant: "Merchant data matches",
      sameCustomer: "Customer data matches",
      allPagesPresent: "All pages are present",
      productDataClear: "Product/service data is clear",
      priceAndScheduleMatchSystem: "Price and schedule match the system",
      platformFeeVisibleAsIndependentClause: "Platform fee appears as an independent clause",
      merchantSignaturePresent: "Merchant signature is present",
      customerSignaturePresent: "Customer signature is present",
      stampPresentIfRequired: "Stamp is present when required",
      scanQualityAcceptable: "File quality is acceptable",
      noUnacceptableEdits: "No unacceptable edits",
      acceptableForApproval: "Acceptable for approval"
    },
    notes: {
      consistencyWarnings: "Consistency warnings:",
      authenticatedFiles:
        "Files open only through authenticated backend endpoints. Public storage links are not displayed.",
      approvalDoesNotActivate:
        "Contract approval is the only administrator approval. Activation then happens automatically after the platform fee and remaining required gates are complete.",
      customerReceiptBoundary:
        "This record confirms what the merchant collected from the customer. It does not confirm the merchant's separate remittance to the platform.",
      feeConfirmation:
        "Manual confirmation is only for independently verified merchant-to-platform remittance. After remittance and contract approval are both verified, activation runs automatically with no separate administrator action.",
      automaticActivation:
        "Automatic activation remains blocked until every backend gate is verified. No temporary local status is created.",
      noRegisteredDispute: "No registered dispute",
      disputeFallback: "Full dispute workflow is planned for a later phase."
    }
  },
  ar: {
    nav: "طلبات التقسيط",
    all: "الكل",
    complete: "مكتمل",
    incomplete: "غير مكتمل",
    yes: "نعم",
    no: "لا",
    notPresent: "غير موجود",
    secureAvailable: "متاح عبر نقطة وصول آمنة",
    unavailable: "غير متاح",
    eyebrow: "مساحة متابعة العمليات",
    title: "طلبات التقسيط",
    subtitle: "إدارة ومتابعة طلبات التقسيط والعقود ورسوم المنصة",
    total: "الإجمالي",
    refresh: "تحديث",
    rowsPerPage: "عدد الصفوف في الصفحة",
    page: "صفحة",
    previous: "السابق",
    next: "التالي",
    searchPlaceholder: "بحث في العمليات...",
    summary: {
      total: "إجمالي العمليات",
      waitingReview: "بانتظار مراجعة العقد",
      reuploadRequired: "مطلوب إعادة رفع",
      contractApproved: "عقود معتمدة",
      waitingPlatformFee: "بانتظار رسوم المنصة",
      active: "عمليات نشطة",
      inDispute: "محل نزاع"
    },
    filters: {
      operationReference: "رقم العملية",
      operationStatus: "حالة العملية",
      contractReviewStatus: "مراجعة العقد",
      platformFeeStatus: "رسوم المنصة",
      operationDate: "تاريخ العملية",
      allDates: "كل التواريخ",
      today: "اليوم",
      last7Days: "آخر 7 أيام",
      last30Days: "آخر 30 يوماً",
      last90Days: "آخر 90 يوماً",
      customRange: "نطاق مخصص",
      customOperationRange: "نطاق تاريخ العملية المخصص",
      fromDate: "من تاريخ",
      toDate: "إلى تاريخ",
      editCustomRange: "تعديل نطاق التاريخ المخصص",
      apply: "تطبيق",
      cancel: "إلغاء",
      showFilters: "إظهار عوامل التصفية",
      hideFilters: "إخفاء عوامل التصفية",
      reset: "مسح الفلاتر"
    },
    columns: {
      operationReference: "رقم العملية",
      customer: "المشتري",
      merchant: "التاجر",
      product: "المنتج/الخدمة",
      cashPrice: "السعر النقدي",
      finalDeferredSalePrice: "الثمن الآجل النهائي",
      platformFee: "رسوم المنصة",
      operationStatus: "حالة العملية",
      contract: "العقد",
      dispute: "النزاع",
      merchantRejection: "رفض التاجر",
      contractSerial: "رقم العقد",
      updatedAt: "آخر تحديث",
      action: "إجراء"
    },
    buttons: {
      viewDetails: "عرض التفاصيل",
      viewGenerated: "عرض العقد الأصلي",
      downloadGenerated: "تنزيل العقد الأصلي",
      viewSigned: "عرض العقد الموقع",
      downloadSigned: "تنزيل العقد الموقع",
      viewSignedFile: "عرض الملف الموقع",
      downloadSignedFile: "تحميل الملف الموقع",
      approveContract: "اعتماد العقد",
      rejectContract: "رفض العقد",
      confirmPlatformFee: "تأكيد رسوم المنصة"
    },
    detailTitle: "تفاصيل العملية",
    drawer: {
      eyebrow: "مسار العملية",
      currentStage: "المرحلة الحالية",
      chooseReviewDecision: "قرار المراجعة",
      approve: "اعتماد",
      reject: "رفض",
      saveReview: "حفظ قرار المراجعة",
      reviewComplete: "مراجعة العقد للقراءة فقط في الحالة الحالية.",
      feeComplete: "تم استكمال رسوم المنصة، لذلك تم إخفاء حقول التأكيد اليدوي.",
      feeUnavailable: "التأكيد اليدوي لتحويل التاجر رسوم المنصة غير متاح في الحالة الحالية.",
      optionalDetails: "تفاصيل تأكيد اختيارية",
      noActionRequired: "لا توجد مدخلات مطلوبة من المسؤول في هذه المرحلة.",
      automaticActivationWaiting:
        "ستُفعّل العملية تلقائيًا عند التحقق من اعتماد العقد واكتمال رسوم المنصة.",
      automaticActivationInProgress:
        "اكتملت متطلبات التفعيل، وتعمل الخلفية الآن على تفعيل العملية تلقائيًا.",
      automaticActivationComplete:
        "تم تفعيل العملية تلقائيًا وأصبحت متابعة الحالة النشطة متاحة."
    },
    tabs: {
      overview: "نظرة عامة",
      files: "ملفات العقد",
      review: "مراجعة العقد",
      fee: "تحويل التاجر رسوم المنصة",
      activation: "التفعيل",
      events: "سجل الأحداث",
      dispute: "النزاع"
    },
    section: {
      statusDimensions: "أبعاد الحالة",
      partiesProduct: "الأطراف والمنتج",
      pricesSchedule: "الأسعار والجدول المخطط",
      customerReceipts: "المبلغ الأولي المستلم من العميل بواسطة التاجر",
      activationReadiness: "متطلبات التفعيل التلقائي",
      merchantRejection: "رفض / إلغاء التاجر",
      contractFiles: "ملفات العقد",
      signedContract: "العقد الموقع",
      uploadAttempts: "سجل محاولات الرفع",
      reviewChecklist: "قائمة مراجعة العقد",
      platformFee: "تحويل التاجر رسوم المنصة",
      manualConfirmation: "تأكيد التحويل يدوياً",
      activationGates: "بوابات التفعيل",
      events: "سجل الأحداث",
      dispute: "النزاع"
    },
    detail: {
      operationReference: "رقم العملية",
      customer: "المشتري",
      customerPhone: "رقم المشتري",
      nationalId: "الرقم القومي",
      merchant: "التاجر",
      merchantPhone: "رقم التاجر",
      product: "المنتج/الخدمة",
      cashPrice: "السعر النقدي",
      finalDeferredSalePrice: "الثمن الآجل النهائي",
      downPayment: "الدفعة المقدمة",
      downPaymentReceipt: "إيصال الدفعة المقدمة",
      customerPlatformFeeReceipt: "إيصال رسوم المنصة من العميل",
      receiptStatus: "حالة الإيصال",
      recordedAt: "تاريخ التسجيل",
      receiptReference: "مرجع الاستلام",
      receiptNote: "ملاحظة الاستلام",
      platformFeePercentage: "نسبة رسوم المنصة",
      platformFee: "رسوم المنصة",
      installmentCount: "عدد الأقساط",
      installmentAmount: "قيمة القسط",
      signedUpload: "رفع العقد الموقع",
      adminApproval: "اعتماد مسؤول المنصة",
      generatedFile: "ملف العقد الأصلي",
      canActivate: "قابلية التفعيل",
      contractSerial: "رقم العقد",
      contractStatus: "حالة العقد",
      contractReview: "مراجعة العقد",
      attemptNumber: "رقم المحاولة",
      uploadStatus: "حالة الرفع",
      uploadedAt: "تاريخ الرفع",
      uploadAttemptsCount: "عدد محاولات الرفع",
      uploader: "الرافع",
      file: "الملف",
      rejectionReason: "سبب الرفض",
      rejectionStage: "مرحلة الرفض",
      rejectedBy: "تم الرفض بواسطة",
      rejectedAt: "تاريخ الرفض",
      visibleToCustomer: "ظاهر للمشتري",
      visibleToAdmin: "ظاهر للمسؤول",
      latestAttempt: "أحدث محاولة",
      action: "إجراء",
      reviewReason: "سبب الرفض",
      reviewNotes: "ملاحظات المراجعة",
      feeAmount: "قيمة الرسوم",
      percentage: "النسبة",
      baseType: "نوع الأساس",
      baseAmount: "قيمة الأساس",
      platformFeeStatus: "حالة رسوم المنصة",
      paymentProvider: "مزود السداد",
      paymentReference: "مرجع السداد",
      manualConfirmation: "تأكيد يدوي",
      paymentMethod: "طريقة السداد",
      note: "ملاحظة",
      confirmationDate: "تاريخ التأكيد",
      amount: "القيمة",
      currency: "العملة",
      activeTrackingGate: "متابعة الحالة النشطة",
      missingGates: "بوابات غير مكتملة:",
      event: "الحدث",
      role: "الدور",
      date: "التاريخ",
      safeSummary: "ملخص آمن",
      disputeStatus: "حالة النزاع",
      hasDispute: "يوجد نزاع"
    },
    checklist: {
      serialMatchesOperation: "رقم العقد مطابق للعملية",
      sameMerchant: "بيانات التاجر مطابقة",
      sameCustomer: "بيانات المشتري مطابقة",
      allPagesPresent: "كل الصفحات موجودة",
      productDataClear: "بيانات المنتج/الخدمة واضحة",
      priceAndScheduleMatchSystem: "السعر والجدول مطابقان للنظام",
      platformFeeVisibleAsIndependentClause: "رسوم المنصة ظاهرة كبند مستقل",
      merchantSignaturePresent: "توقيع التاجر موجود",
      customerSignaturePresent: "توقيع المشتري موجود",
      stampPresentIfRequired: "الختم موجود عند الحاجة",
      scanQualityAcceptable: "جودة الملف مقبولة",
      noUnacceptableEdits: "لا توجد تعديلات غير مقبولة",
      acceptableForApproval: "مقبول للاعتماد"
    },
    notes: {
      consistencyWarnings: "تنبيهات اتساق:",
      authenticatedFiles:
        "الملفات تفتح من نقاط وصول خلفية موثقة فقط، ولا يتم عرض روابط تخزين عامة.",
      approvalDoesNotActivate:
        "اعتماد العقد هو الاعتماد الوحيد المطلوب من المسؤول، ثم يتم التفعيل تلقائيًا بعد اكتمال رسوم المنصة وباقي المتطلبات اللازمة.",
      customerReceiptBoundary:
        "هذا السجل يؤكد ما استلمه التاجر من العميل، ولا يؤكد تحويل التاجر المستقل لرسوم المنصة إلى المنصة.",
      feeConfirmation:
        "التأكيد اليدوي مخصص فقط لتحويل التاجر إلى المنصة بعد التحقق منه بشكل مستقل. بعد التحقق من التحويل واعتماد العقد يتم التفعيل تلقائيًا دون إجراء منفصل من المسؤول.",
      automaticActivation:
        "يبقى التفعيل التلقائي محظورًا حتى تتحقق الخلفية من جميع المتطلبات، ولا يتم إنشاء حالة محلية مؤقتة.",
      noRegisteredDispute: "لا يوجد نزاع مسجل",
      disputeFallback: "مسار النزاع الكامل مخطط لمرحلة لاحقة."
    }
  }
};

const FILTER_OPTIONS = {
  operationStatus: OPERATION_STATUS_FILTER_OPTIONS,
  contractReviewStatus: [
    "NOT_SUBMITTED",
    "PENDING_ADMIN_REVIEW",
    "APPROVED",
    "REJECTED",
    "REUPLOAD_REQUIRED"
  ],
  platformFeeStatus: [
    "PENDING_PAYMENT",
    "PAYMENT_REQUESTED",
    "PAID",
    "MANUALLY_CONFIRMED",
    "FAILED",
    "REFUNDED"
  ]
};

const badgeVariant = (value) => {
  if (["APPROVED", "CONTRACT_APPROVED", "PAID", "MANUALLY_CONFIRMED", "COMPLETED"].includes(value)) {
    return "success";
  }
  if (["REJECTED", "CONTRACT_REJECTED", "REUPLOAD_REQUIRED", "FAILED", "IN_DISPUTE", "CANCELLED"].includes(value)) {
    return "danger";
  }
  if (["ACTIVE_INSTALLMENT_OPERATION"].includes(value)) return "primary";
  if (["PENDING_PAYMENT", "PENDING_ADMIN_REVIEW", "WAITING_FOR_PLATFORM_FEE_PAYMENT", "WAITING_FOR_PLATFORM_ADMIN_CONTRACT_REVIEW"].includes(value)) {
    return "warning";
  }
  return "secondary";
};

const formatStatus = (value, statusLabels) => statusLabels[value] || value || "-";

const shortId = (value) => {
  const id = String(value || "");
  if (!id) return "-";
  return id.length > 10 ? `${id.slice(0, 6)}...${id.slice(-4)}` : id;
};

const formatMoney = (value, currency = "EGP", language = "en") => {
  if (value === undefined || value === null || value === "") return "-";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return `${numericValue.toLocaleString(language === "ar" ? "ar-EG" : "en-US")} ${currency}`;
};

const formatDate = (value, language = "en") => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
};

const StatusBadge = ({ value, statusLabels }) => (
  <Badge bg={badgeVariant(value)} className="operations-status-badge">
    {formatStatus(value, statusLabels)}
  </Badge>
);

const SummaryCard = ({ label, value, loading, icon, tone }) => (
  <article className={`operations-summary-card is-${tone}`}>
    <span className="operations-summary-icon" aria-hidden="true">
      <i className={`fa-solid ${icon}`} />
    </span>
    <div>
      <span>{label}</span>
      <strong>{loading ? "..." : value ?? 0}</strong>
    </div>
  </article>
);

const FilterSelect = ({ label, name, value, options, onChange, text, statusLabels }) => (
  <label className={`account-filter-field operations-filter operations-${name}-filter`}>
    <span>{label}</span>
    <select
      value={value}
      onChange={(event) => onChange(name, event.target.value)}
    >
      <option value="">{text.all}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {formatStatus(option, statusLabels)}
        </option>
      ))}
    </select>
  </label>
);

const OperationFilters = ({
  filters,
  searchDraft,
  setSearchDraft,
  updateFilter,
  updateFilters,
  resetFilters,
  text,
  statusLabels
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customRange, setCustomRange] = useState({ from: "", to: "" });

  useEffect(() => {
    setCustomRange({ from: filters.dateFrom || "", to: filters.dateTo || "" });
  }, [filters.dateFrom, filters.dateTo]);

  const active = hasActiveOperationFilters(filters, searchDraft);
  const validCustomRange = Boolean(
    customRange.from && customRange.to && customRange.from <= customRange.to
  );
  const selectedCustomRange = [filters.dateFrom, filters.dateTo]
    .filter(Boolean)
    .join(" — ");
  const changeDatePreset = (value) => {
    if (value === "custom") {
      setShowCustomRange(true);
      return;
    }
    updateFilters({ datePreset: value, dateFrom: "", dateTo: "" });
  };
  const applyCustomRange = () => {
    if (!validCustomRange) return;
    updateFilters({
      datePreset: "custom",
      dateFrom: customRange.from,
      dateTo: customRange.to
    });
    setShowCustomRange(false);
  };

  return (
    <>
      <div className="operations-filter-shell">
        <button
          type="button"
          className="operations-filter-toggle"
          aria-expanded={expanded}
          aria-controls="installment-operation-filters"
          onClick={() => setExpanded((value) => !value)}
        >
          <span>
            <i className="fa-solid fa-sliders" aria-hidden="true" />
            {expanded ? text.filters.hideFilters : text.filters.showFilters}
          </span>
          {active && <span className="operations-filter-active-dot" aria-hidden="true" />}
          <i
            className={`fa-solid fa-chevron-${expanded ? "up" : "down"}`}
            aria-hidden="true"
          />
        </button>
        <div
          id="installment-operation-filters"
          className={`operations-filter-panel ${expanded ? "is-expanded" : ""}`}
          aria-label={text.filters.operationStatus}
        >
          <label className="account-search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input
              type="search"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              placeholder={text.searchPlaceholder}
            />
          </label>
          <label className="account-filter-field operations-reference-filter">
            <span>{text.filters.operationReference}</span>
            <input
              type="text"
              aria-label={text.filters.operationReference}
              value={filters.operationReference || ""}
              onChange={(event) =>
                updateFilter("operationReference", event.target.value)
              }
            />
          </label>
          <FilterSelect
            label={text.filters.operationStatus}
            name="operationStatus"
            value={filters.operationStatus}
            options={FILTER_OPTIONS.operationStatus}
            onChange={updateFilter}
            text={text}
            statusLabels={statusLabels}
          />
          <FilterSelect
            label={text.filters.contractReviewStatus}
            name="contractReviewStatus"
            value={filters.contractReviewStatus}
            options={FILTER_OPTIONS.contractReviewStatus}
            onChange={updateFilter}
            text={text}
            statusLabels={statusLabels}
          />
          <FilterSelect
            label={text.filters.platformFeeStatus}
            name="platformFeeStatus"
            value={filters.platformFeeStatus}
            options={FILTER_OPTIONS.platformFeeStatus}
            onChange={updateFilter}
            text={text}
            statusLabels={statusLabels}
          />
          <div className="account-filter-field operations-date-filter">
            <span>{text.filters.operationDate}</span>
            <div className="operations-date-filter-control">
              <select
                aria-label={text.filters.operationDate}
                className={filters.datePreset === "custom" ? "has-custom-range" : ""}
                value={filters.datePreset || "all"}
                onChange={(event) => changeDatePreset(event.target.value)}
              >
                <option value="all">{text.filters.allDates}</option>
                <option value="today">{text.filters.today}</option>
                <option value="last7">{text.filters.last7Days}</option>
                <option value="last30">{text.filters.last30Days}</option>
                <option value="last90">{text.filters.last90Days}</option>
                <option value="custom">
                  {filters.datePreset === "custom" && selectedCustomRange
                    ? selectedCustomRange
                    : text.filters.customRange}
                </option>
              </select>
              {filters.datePreset === "custom" && (
                <button
                  type="button"
                  className="operations-custom-range-edit"
                  onClick={() => setShowCustomRange(true)}
                  aria-label={text.filters.editCustomRange}
                >
                  <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            className={`account-clear-filters ${active ? "" : "is-placeholder"}`}
            onClick={resetFilters}
            disabled={!active}
            tabIndex={active ? 0 : -1}
            aria-hidden={!active}
          >
            {text.filters.reset}
          </button>
        </div>
      </div>

      <Modal
        show={showCustomRange}
        onHide={() => setShowCustomRange(false)}
        centered
        aria-labelledby="operation-date-range-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="operation-date-range-title">
            {text.filters.customOperationRange}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="operations-date-range">
            <label className="account-filter-field">
              <span>{text.filters.fromDate}</span>
              <input
                aria-label={text.filters.fromDate}
                type="date"
                value={customRange.from}
                max={customRange.to || undefined}
                onChange={(event) => setCustomRange((current) => ({
                  ...current,
                  from: event.target.value
                }))}
              />
            </label>
            <label className="account-filter-field">
              <span>{text.filters.toDate}</span>
              <input
                aria-label={text.filters.toDate}
                type="date"
                value={customRange.to}
                min={customRange.from || undefined}
                onChange={(event) => setCustomRange((current) => ({
                  ...current,
                  to: event.target.value
                }))}
              />
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCustomRange(false)}>
            {text.filters.cancel}
          </Button>
          <Button variant="primary" disabled={!validCustomRange} onClick={applyCustomRange}>
            {text.filters.apply}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const DetailItem = ({ label, children }) => (
  <div className="operation-detail-item">
    <span>{label}</span>
    <strong>
      {children === undefined || children === null || children === ""
        ? "-"
        : children}
    </strong>
  </div>
);

const GateValue = ({ active, text }) => (
  <Badge
    bg={active ? "success" : "secondary"}
    className="operation-gate-badge"
  >
    {active ? text.complete : text.incomplete}
  </Badge>
);

const DRAWER_TAB_ICONS = {
  overview: "fa-table-cells-large",
  files: "fa-file-contract",
  review: "fa-clipboard-check",
  fee: "fa-receipt",
  activation: "fa-circle-check",
  events: "fa-clock-rotate-left",
  dispute: "fa-triangle-exclamation"
};

const OperationDrawerHeader = ({
  operation,
  text,
  statusLabels
}) => (
  <section className="operation-drawer-summary">
    <div className="operation-drawer-summary-copy">
      <span>{text.drawer.currentStage}</span>
      <h2>{operation.product?.name || text.detailTitle}</h2>
      <p>
        {getPublicOperationReference(operation)}
        {operation.generatedContract?.serial
          ? ` · ${operation.generatedContract.serial}`
          : ""}
      </p>
    </div>
    <StatusBadge
      value={operation.operationStatus || operation.primaryOperationStatus}
      statusLabels={statusLabels}
    />
  </section>
);

const OperationDrawerTabs = ({
  tabs,
  activeTab,
  onChange,
  text
}) => (
  <nav
    className="account-drawer-tabs operations-detail-tabs"
    aria-label={text.detailTitle}
  >
    {tabs.map((tab) => (
      <button
        type="button"
        key={tab}
        className={activeTab === tab ? "is-active" : ""}
        aria-pressed={activeTab === tab}
        onClick={() => onChange(tab)}
      >
        <i
          className={`fa-solid ${DRAWER_TAB_ICONS[tab]}`}
          aria-hidden="true"
        />
        {text.tabs[tab]}
      </button>
    ))}
  </nav>
);

const OverviewTab = ({ operation, text, statusLabels, language }) => {
  const receipts =
    operation.initialPaymentReceipt || operation.customerPaymentReceipts;
  const downPaymentReceipt = receipts?.downPayment;
  const platformFeeReceipt = receipts?.platformFee;
  const hasInitialPaymentReceipt = Boolean(
    downPaymentReceipt || platformFeeReceipt
  );
  const receiptRecordedAt =
    downPaymentReceipt?.recordedAt || platformFeeReceipt?.recordedAt;
  const receiptReference =
    downPaymentReceipt?.reference || platformFeeReceipt?.reference;
  const receiptNote = downPaymentReceipt?.note || platformFeeReceipt?.note;
  const hasPricing = [
    operation.prices?.cashPrice,
    operation.prices?.finalDeferredSalePrice,
    operation.installmentSchedule?.installmentCount
  ].some((value) => value !== undefined && value !== null);
  const hasActivationReadiness = Boolean(
    operation.activationGates &&
      (operation.generatedContract?.contract ||
        Object.values(operation.activationGates).some(Boolean))
  );

  return (
    <div className="operation-detail-panel">
      <h6>{text.section.statusDimensions}</h6>
      <div className="operation-badge-row">
        <StatusBadge value={operation.operationStatus} statusLabels={statusLabels} />
        {operation.contractReviewStatus && (
          <StatusBadge
            value={operation.contractReviewStatus}
            statusLabels={statusLabels}
          />
        )}
        {operation.platformFeeStatus && (
          <StatusBadge
            value={operation.platformFeeStatus}
            statusLabels={statusLabels}
          />
        )}
      </div>

      {operation.merchantRejection && (
        <>
          <h6>{text.section.merchantRejection}</h6>
          <div className="operation-detail-grid">
            <DetailItem label={text.detail.rejectionStage}>
              {formatStatus(operation.merchantRejection.stage, statusLabels)}
            </DetailItem>
            <DetailItem label={text.detail.rejectionReason}>
              {operation.merchantRejection.reason}
            </DetailItem>
            <DetailItem label={text.detail.rejectedBy}>
              {shortId(operation.merchantRejection.rejectedBy)}
            </DetailItem>
            <DetailItem label={text.detail.rejectedAt}>
              {formatDate(operation.merchantRejection.rejectedAt, language)}
            </DetailItem>
          </div>
        </>
      )}

      <h6>{text.section.partiesProduct}</h6>
      <div className="operation-detail-grid">
        <DetailItem label={text.detail.operationReference}>
          {getPublicOperationReference(operation)}
        </DetailItem>
        <DetailItem label={text.detail.customer}>
          {operation.customer?.fullName}
        </DetailItem>
        <DetailItem label={text.detail.customerPhone}>
          {operation.customer?.phone}
        </DetailItem>
        <DetailItem label={text.detail.merchant}>
          {operation.merchant?.name}
        </DetailItem>
        <DetailItem label={text.detail.merchantPhone}>
          {operation.merchant?.phone}
        </DetailItem>
        <DetailItem label={text.detail.product}>{operation.product?.name}</DetailItem>
      </div>

      {hasPricing && (
        <>
          <h6>{text.section.pricesSchedule}</h6>
          <div className="operation-detail-grid">
            <DetailItem label={text.detail.cashPrice}>
              {formatMoney(operation.prices?.cashPrice, "EGP", language)}
            </DetailItem>
            <DetailItem label={text.detail.finalDeferredSalePrice}>
              {formatMoney(
                operation.prices?.finalDeferredSalePrice,
                "EGP",
                language
              )}
            </DetailItem>
            <DetailItem label={text.detail.downPayment}>
              {formatMoney(operation.prices?.downPayment, "EGP", language)}
            </DetailItem>
            <DetailItem label={text.detail.platformFee}>
              {formatMoney(
                operation.platformFee?.expectedFeeAmount ||
                  operation.platformFeeAmount,
                operation.platformFee?.currency || "EGP",
                language
              )}
            </DetailItem>
            <DetailItem label={text.detail.installmentCount}>
              {operation.installmentSchedule?.installmentCount}
            </DetailItem>
            <DetailItem label={text.detail.installmentAmount}>
              {formatMoney(
                operation.installmentSchedule?.installmentAmount,
                "EGP",
                language
              )}
            </DetailItem>
          </div>
        </>
      )}

      {hasInitialPaymentReceipt && (
        <>
          <h6>{text.section.customerReceipts}</h6>
          <div className="operation-detail-grid">
            <DetailItem label={text.detail.downPaymentReceipt}>
              {formatStatus(downPaymentReceipt?.status, statusLabels)}
              {downPaymentReceipt?.amount !== undefined && (
                <>
                  {" · "}
                  {formatMoney(
                    downPaymentReceipt.amount,
                    downPaymentReceipt.currency || "EGP",
                    language
                  )}
                </>
              )}
            </DetailItem>
            <DetailItem label={text.detail.customerPlatformFeeReceipt}>
              {formatStatus(platformFeeReceipt?.status, statusLabels)}
              {platformFeeReceipt?.amount !== undefined && (
                <>
                  {" · "}
                  {formatMoney(
                    platformFeeReceipt.amount,
                    platformFeeReceipt.currency || "EGP",
                    language
                  )}
                </>
              )}
            </DetailItem>
            <DetailItem label={text.detail.recordedAt}>
              {formatDate(receiptRecordedAt, language)}
            </DetailItem>
            {receiptReference && (
              <DetailItem label={text.detail.receiptReference}>
                {receiptReference}
              </DetailItem>
            )}
            {receiptNote && (
              <DetailItem label={text.detail.receiptNote}>
                {receiptNote}
              </DetailItem>
            )}
          </div>
          <div className="operation-workflow-notice is-info">
            {text.notes.customerReceiptBoundary}
          </div>
        </>
      )}

      {hasActivationReadiness && (
        <>
          <h6>{text.section.activationReadiness}</h6>
          <div className="operation-detail-grid">
            <DetailItem label={text.detail.signedUpload}>
              <GateValue
                active={operation.activationGates?.signedUploadGate}
                text={text}
              />
            </DetailItem>
            <DetailItem label={text.detail.adminApproval}>
              <GateValue
                active={operation.activationGates?.adminApprovalGate}
                text={text}
              />
            </DetailItem>
            <DetailItem label={text.detail.platformFee}>
              <GateValue
                active={operation.activationGates?.platformFeeGate}
                text={text}
              />
            </DetailItem>
          </div>
        </>
      )}

      {operation.activationConsistency?.warnings?.length > 0 && (
        <div className="operation-workflow-notice is-warning">
          <strong>{text.notes.consistencyWarnings}</strong>
          <ul className="mb-0">
            {operation.activationConsistency.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const ContractFilesTab = ({
  contract,
  operation,
  contractFileApi,
  markReviewedAttempt,
  text,
  statusLabels,
  language
}) => {
  const signedAttempt =
    contract?.latestSignedUploadAttempt || operation?.signedUpload;
  const uploadAttempts = contract?.uploadAttempts || [];
  return (
    <div className="operation-detail-panel">
      <h6>{text.section.contractFiles}</h6>
      <DetailItem label={text.detail.contractSerial}>
        {contract?.serial || operation?.generatedContract?.serial}
      </DetailItem>
      <DetailItem label={text.detail.contractStatus}>
        {formatStatus(contract?.status, statusLabels)}
      </DetailItem>
      <DetailItem label={text.detail.contractReview}>
        {formatStatus(contract?.reviewStatus, statusLabels)}
      </DetailItem>
      <ContractPdfWorkspace
        key={`${contract?.contract || operation?.generatedContract?.contract}:${signedAttempt?.id || "none"}`}
        operation={{ ...operation, contract }}
        api={contractFileApi}
        locale={language}
        onSignedViewed={markReviewedAttempt}
      />

      {(signedAttempt || uploadAttempts.length > 0) && (
        <>
          <h6>{text.section.signedContract}</h6>
          <div className="operation-detail-grid">
            <DetailItem label={text.detail.attemptNumber}>
              {signedAttempt?.attemptNumber}
            </DetailItem>
            <DetailItem label={text.detail.uploadStatus}>
              {formatStatus(signedAttempt?.uploadStatus, statusLabels)}
            </DetailItem>
            <DetailItem label={text.detail.uploadedAt}>
              {formatDate(signedAttempt?.uploadedAt, language)}
            </DetailItem>
            <DetailItem label={text.detail.uploadAttemptsCount}>
              {contract?.uploadAttemptsCount || (signedAttempt ? 1 : 0)}
            </DetailItem>
          </div>
        </>
      )}
      {uploadAttempts.length > 0 && (
        <>
          <h6>{text.section.uploadAttempts}</h6>
          <div className="operations-drawer-table-scroll">
            <Table hover className="text-center operations-attempts-table">
              <thead>
                <tr>
                  <th>{text.detail.attemptNumber}</th>
                  <th>{text.detail.uploadStatus}</th>
                  <th>{text.detail.uploadedAt}</th>
                  <th>{text.detail.uploader}</th>
                  <th>{text.detail.file}</th>
                  <th>{text.detail.rejectionReason}</th>
                  <th>{text.detail.latestAttempt}</th>
                </tr>
              </thead>
              <tbody>
                {uploadAttempts.map((attempt) => (
              <tr key={attempt.id}>
                <td>{attempt.attemptNumber || "-"}</td>
                <td>
                  <StatusBadge value={attempt.uploadStatus} statusLabels={statusLabels} />
                </td>
                <td>{formatDate(attempt.uploadedAt, language)}</td>
                <td>
                  <div>{attempt.uploadedBy?.name || attempt.uploadedBy?.id || "-"}</div>
                  <small>{attempt.uploaderRole || ""}</small>
                </td>
                <td>
                  <div>{attempt.mimeType || "-"}</div>
                  <small>{attempt.byteSize ? `${attempt.byteSize} bytes` : ""}</small>
                </td>
                <td>
                  <div>{attempt.rejectionReason || "-"}</div>
                  <small>{formatDate(attempt.rejectedAt, language)}</small>
                </td>
                <td>{attempt.isLatestAttempt ? text.yes : text.no}</td>
              </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      )}
      <div className="operation-workflow-notice is-info">
        {text.notes.authenticatedFiles}
      </div>
    </div>
  );
};

const PlatformFeeTab = ({
  fee,
  operation,
  manualFeeForm,
  updateManualFeeForm,
  submitManualFeeConfirmation,
  actionLoading,
  text,
  statusLabels,
  language
}) => (
  <PlatformFeeContent
    fee={fee}
    operation={operation}
    manualFeeForm={manualFeeForm}
    updateManualFeeForm={updateManualFeeForm}
    submitManualFeeConfirmation={submitManualFeeConfirmation}
    actionLoading={actionLoading}
    text={text}
    statusLabels={statusLabels}
    language={language}
  />
);

const PlatformFeeContent = ({
  fee,
  operation,
  manualFeeForm,
  updateManualFeeForm,
  submitManualFeeConfirmation,
  actionLoading,
  text,
  statusLabels,
  language
}) => {
  const remittance =
    fee ||
    operation?.merchantToPlatformRemittance ||
    operation?.platformFee ||
    {};
  const capabilities = getOperationCapabilities({
    operation,
    fee: remittance
  });
  const feeState =
    remittance.feeState ||
    operation?.platformFeeStatus;

  return (
    <div className="operation-detail-panel">
      <h6>{text.section.platformFee}</h6>
      <div className="operation-detail-grid">
        <DetailItem label={text.detail.feeAmount}>
          {formatMoney(
            remittance.expectedFeeAmount,
            remittance.currency || "EGP",
            language
          )}
        </DetailItem>
        <DetailItem label={text.detail.percentage}>
          {remittance.feePercentage !== undefined
            ? `${remittance.feePercentage}%`
            : "-"}
        </DetailItem>
        <DetailItem label={text.detail.baseAmount}>
          {formatMoney(
            remittance.feeBaseAmount,
            remittance.currency || "EGP",
            language
          )}
        </DetailItem>
        <DetailItem label={text.detail.platformFeeStatus}>
          {formatStatus(feeState, statusLabels)}
        </DetailItem>
        {remittance.paymentProvider && (
          <DetailItem label={text.detail.paymentProvider}>
            {remittance.paymentProvider}
          </DetailItem>
        )}
        {(remittance.providerReference ||
          remittance.manualConfirmation?.reference) && (
          <DetailItem label={text.detail.paymentReference}>
            {remittance.providerReference ||
              remittance.manualConfirmation.reference}
          </DetailItem>
        )}
      </div>

      {capabilities.canConfirmPlatformFee ? (
        <>
          <h6>{text.section.manualConfirmation}</h6>
          <Form
            className="operation-smart-form"
            onSubmit={submitManualFeeConfirmation}
          >
            <div className="operation-form-grid">
              <Form.Group className="operation-form-field">
                <Form.Label>{text.detail.paymentMethod} *</Form.Label>
                <Form.Control
                  required
                  value={manualFeeForm.method}
                  onChange={(event) =>
                    updateManualFeeForm("method", event.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="operation-form-field">
                <Form.Label>{text.detail.paymentReference} *</Form.Label>
                <Form.Control
                  required
                  value={manualFeeForm.reference}
                  onChange={(event) =>
                    updateManualFeeForm("reference", event.target.value)
                  }
                />
              </Form.Group>
            </div>
            <details className="operation-optional-fields">
              <summary>{text.drawer.optionalDetails}</summary>
              <div className="operation-form-grid">
                <Form.Group className="operation-form-field is-wide">
                  <Form.Label>{text.detail.note}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={manualFeeForm.note}
                    onChange={(event) =>
                      updateManualFeeForm("note", event.target.value)
                    }
                  />
                </Form.Group>
                <Form.Group className="operation-form-field">
                  <Form.Label>{text.detail.confirmationDate}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={manualFeeForm.paidAt}
                    onChange={(event) =>
                      updateManualFeeForm("paidAt", event.target.value)
                    }
                  />
                </Form.Group>
                <Form.Group className="operation-form-field">
                  <Form.Label>{text.detail.amount}</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualFeeForm.amount}
                    onChange={(event) =>
                      updateManualFeeForm("amount", event.target.value)
                    }
                  />
                </Form.Group>
                <Form.Group className="operation-form-field">
                  <Form.Label>{text.detail.currency}</Form.Label>
                  <Form.Control
                    value={manualFeeForm.currency}
                    onChange={(event) =>
                      updateManualFeeForm("currency", event.target.value)
                    }
                  />
                </Form.Group>
              </div>
            </details>
            <Button
              type="submit"
              className="operation-primary-action"
              disabled={actionLoading === "manual-fee"}
            >
              {actionLoading === "manual-fee" && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                />
              )}
              {text.buttons.confirmPlatformFee}
            </Button>
          </Form>
        </>
      ) : (
        <div className="operation-workflow-notice is-info">
          {capabilities.feeComplete
            ? text.drawer.feeComplete
            : text.drawer.feeUnavailable}
        </div>
      )}
      <div className="operation-workflow-notice is-info">
        {text.notes.feeConfirmation}
      </div>
    </div>
  );
};

const ActivationTab = ({ operation, text }) => {
  const capabilities = getOperationCapabilities({ operation });
  const activationMessage = capabilities.isActive
    ? text.drawer.automaticActivationComplete
    : operation.activationGates?.canActivate
      ? text.drawer.automaticActivationInProgress
      : text.drawer.automaticActivationWaiting;
  return (
    <div className="operation-detail-panel">
      <h6>{text.section.activationGates}</h6>
      <div className="operation-detail-grid">
        <DetailItem label={text.detail.signedUpload}>
          <GateValue
            active={operation.activationGates?.signedUploadGate}
            text={text}
          />
        </DetailItem>
        <DetailItem label={text.detail.adminApproval}>
          <GateValue
            active={operation.activationGates?.adminApprovalGate}
            text={text}
          />
        </DetailItem>
        <DetailItem label={text.detail.platformFee}>
          <GateValue
            active={operation.activationGates?.platformFeeGate}
            text={text}
          />
        </DetailItem>
        <DetailItem label={text.detail.generatedFile}>
          <GateValue
            active={operation.activationGates?.generatedPdfGate}
            text={text}
          />
        </DetailItem>
        <DetailItem label={text.detail.activeTrackingGate}>
          <GateValue
            active={operation.activationGates?.activeTrackingGate}
            text={text}
          />
        </DetailItem>
      </div>
      {operation.activationGates?.missingGates?.length > 0 && (
        <div className="operation-workflow-notice is-warning">
          <strong>{text.detail.missingGates}</strong>
          <ul className="mb-0">
            {operation.activationGates.missingGates.map((gate) => (
              <li key={gate}>{gate}</li>
            ))}
          </ul>
        </div>
      )}
      {operation.activationConsistency?.warnings?.length > 0 && (
        <div className="operation-workflow-notice is-warning">
          <strong>{text.notes.consistencyWarnings}</strong>
          <ul className="mb-0">
            {operation.activationConsistency.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="operation-workflow-notice is-info">
        {activationMessage}
      </div>
      <div className="operation-workflow-notice is-info">
        {text.notes.automaticActivation}
      </div>
    </div>
  );
};

const EventsTab = ({
  events,
  loading,
  eventsPagination,
  eventPage,
  getEventPageNumbers,
  handleEventPageChange,
  text,
  language
}) => (
  <div className="operation-detail-panel">
    <h6>{text.section.events}</h6>
    <Table hover responsive className="text-center">
      <thead>
        <tr>
          <th className="bg-light">{text.detail.event}</th>
          <th className="bg-light">{text.detail.role}</th>
          <th className="bg-light">{text.detail.date}</th>
          <th className="bg-light">{text.detail.safeSummary}</th>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          Array.from({ length: 4 }, (_, index) => (
            <Shimmer key={index} type="table" colSpan={4} />
          ))
        ) : events.length === 0 ? (
          <tr>
            <td colSpan={4}>
              <NoData />
            </td>
          </tr>
        ) : (
          events.map((event) => (
            <tr key={event.id}>
              <td>{event.eventType}</td>
              <td>{event.actorRole || "-"}</td>
              <td>{formatDate(event.createdAt, language)}</td>
              <td>
                {event.payloadSummary?.result ||
                  event.payloadSummary?.decision ||
                  event.payloadSummary?.currency ||
                  "-"}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </Table>
    {events.length > 0 && (
      <div className="d-flex justify-content-center" dir="ltr">
        <Pagination>
          <Pagination.Prev
            onClick={() => handleEventPageChange(eventPage - 1)}
            disabled={eventPage === 1}
          />
          {getEventPageNumbers().map((pageNumber) => (
            <Pagination.Item
              key={pageNumber}
              active={pageNumber === eventPage}
              onClick={() => handleEventPageChange(pageNumber)}
            >
              {pageNumber}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => handleEventPageChange(eventPage + 1)}
            disabled={eventPage === (eventsPagination.totalPages || 1)}
          />
        </Pagination>
      </div>
    )}
  </div>
);

const DisputeTab = ({ dispute, operation, text, statusLabels }) => (
  <div className="operation-detail-panel">
    <h6>{text.section.dispute}</h6>
    <DetailItem label={text.detail.disputeStatus}>
      {formatStatus(dispute?.disputeStatus || operation.disputeStatus || "NONE", statusLabels)}
    </DetailItem>
    <DetailItem label={text.detail.hasDispute}>
      {dispute?.hasDispute ? text.yes : text.notes.noRegisteredDispute}
    </DetailItem>
    <div className="operation-workflow-notice is-info">
      {dispute?.workflow || text.notes.disputeFallback}
    </div>
  </div>
);

const AdminInstallmentOperations = () => {
  const outletContext = useOutletContext() || {};
  const language = outletContext.language || "en";
  const direction =
    outletContext.direction || (language === "ar" ? "rtl" : "ltr");
  const text = PAGE_TEXT[language] || PAGE_TEXT.en;
  const statusLabels = STATUS_LABELS[language] || STATUS_LABELS.en;
  const {
    operations,
    summary,
    summaryLoading,
    summaryWarning,
    loading,
    errorMessage,
    filters,
    searchDraft,
    setSearchDraft,
    pagination,
    currentPage,
    perPage,
    handlePageChange,
    handlePerPageChange,
    updateFilter,
    updateFilters,
    resetFilters,
    openOperationDetails,
    closeOperationDetails,
    selectedOperation,
    selectedContract,
    selectedFee,
    selectedDispute,
    events,
    eventsLoading,
    eventsPagination,
    eventPage,
    getEventPageNumbers,
    handleEventPageChange,
    showDetails,
    detailLoading,
    activeTab,
    setActiveTab,
    contractFileApi,
    actionLoading,
    reviewChecklist,
    reviewForm,
    reviewError,
    reviewedAttemptId,
    setReviewForm,
    updateReviewChecklist,
    markReviewedAttempt,
    submitReview,
    manualFeeForm,
    updateManualFeeForm,
    submitManualFeeConfirmation,
    refresh
  } = AdminInstallmentOperationsHook(language);

  const drawerTabs = selectedOperation
    ? getOperationDrawerTabs({
        operation: selectedOperation,
        contract: selectedContract,
        fee: selectedFee,
        dispute: selectedDispute
      })
    : ["overview"];
  const visibleActiveTab = drawerTabs.includes(activeTab)
    ? activeTab
    : "overview";

  return (
    <>
      <main
        className="user-management-page installment-operations-page"
        dir={direction}
      >
        <section
          className="operations-summary-grid"
          aria-label={text.title}
        >
          <SummaryCard
            label={text.summary.total}
            value={summary.total}
            loading={summaryLoading}
            icon="fa-layer-group"
            tone="navy"
          />
          <SummaryCard
            label={text.summary.waitingReview}
            value={summary.waitingReview}
            loading={summaryLoading}
            icon="fa-file-signature"
            tone="orange"
          />
          <SummaryCard
            label={text.summary.reuploadRequired}
            value={summary.reuploadRequired}
            loading={summaryLoading}
            icon="fa-arrow-rotate-right"
            tone="red"
          />
          <SummaryCard
            label={text.summary.waitingPlatformFee}
            value={summary.waitingPlatformFee}
            loading={summaryLoading}
            icon="fa-receipt"
            tone="gold"
          />
          <SummaryCard
            label={text.summary.active}
            value={summary.active}
            loading={summaryLoading}
            icon="fa-circle-check"
            tone="green"
          />
        </section>

        {summaryWarning && (
          <div className="operation-workflow-notice is-warning">
            {summaryWarning}
          </div>
        )}

        <UserManagementHeader
          eyebrow={text.eyebrow}
          title={text.title}
          subtitle={text.subtitle}
          total={pagination.totalResults || summary.total || 0}
          totalLabel={text.total}
          refreshLabel={text.refresh}
          onRefresh={refresh}
          refreshing={loading || summaryLoading}
        />

        <section className="account-list-card operations-list-card">
          <OperationFilters
            filters={filters}
            searchDraft={searchDraft}
            setSearchDraft={setSearchDraft}
            updateFilter={updateFilter}
            updateFilters={updateFilters}
            resetFilters={resetFilters}
            text={text}
            statusLabels={statusLabels}
          />

          {errorMessage && (
            <div className="operations-inline-error" role="alert">
              {errorMessage}
            </div>
          )}

          <div className="account-table-scroll operations-table-wrap">
            <table className="account-table operations-table">
              <thead>
                <tr>
                  <th>{text.columns.operationReference}</th>
                  <th>{text.columns.customer}</th>
                  <th>{text.columns.merchant}</th>
                  <th>{text.columns.cashPrice}</th>
                  <th>{text.columns.platformFee}</th>
                  <th>{text.columns.operationStatus}</th>
                  <th>{text.columns.contract}</th>
                  <th>{text.columns.updatedAt}</th>
                  <th className="account-table-actions">
                    {text.columns.action}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 6 }, (_, index) => (
                    <Shimmer key={index} type="table" colSpan={9} />
                  ))
                ) : operations.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="account-empty-cell">
                      <NoData />
                    </td>
                  </tr>
                ) : (
                  operations.map((operation) => (
                    <tr key={operation.operation}>
                      <td data-label={text.columns.operationReference}>
                        <div className="operation-primary-cell">
                          <strong>{operation.product?.name || "-"}</strong>
                          <span>
                            {getPublicOperationReference(operation)}
                            {operation.contract?.serial
                              ? ` · ${operation.contract.serial}`
                              : ""}
                          </span>
                        </div>
                      </td>
                      <td data-label={text.columns.customer}>
                        <div className="operation-primary-cell">
                          <strong>{operation.customer?.fullName || "-"}</strong>
                          <span>{operation.customer?.phone || ""}</span>
                        </div>
                      </td>
                      <td data-label={text.columns.merchant}>
                        <div className="operation-primary-cell">
                          <strong>{operation.merchant?.name || "-"}</strong>
                          <span>{operation.merchant?.phone || ""}</span>
                        </div>
                      </td>
                      <td data-label={text.columns.cashPrice}>
                        <div className="operation-price-cell">
                          <strong>
                            {formatMoney(operation.cashPrice, "EGP", language)}
                          </strong>
                          <span>
                            {text.columns.finalDeferredSalePrice}:{" "}
                            {formatMoney(
                              operation.finalDeferredSalePrice,
                              "EGP",
                              language
                            )}
                          </span>
                        </div>
                      </td>
                      <td data-label={text.columns.platformFee}>
                        <div className="operation-price-cell">
                          <strong>
                            {formatMoney(
                              operation.platformFeeAmount,
                              "EGP",
                              language
                            )}
                          </strong>
                          <StatusBadge
                            value={
                              operation.platformFeeState ||
                              operation.platformFeeStatus
                            }
                            statusLabels={statusLabels}
                          />
                        </div>
                      </td>
                      <td data-label={text.columns.operationStatus}>
                        <StatusBadge
                          value={
                            operation.operationStatus ||
                            operation.primaryOperationStatus
                          }
                          statusLabels={statusLabels}
                        />
                      </td>
                      <td data-label={text.columns.contract}>
                        <StatusBadge
                          value={
                            operation.contractReviewStatus ||
                            operation.contractStatus
                          }
                          statusLabels={statusLabels}
                        />
                      </td>
                      <td data-label={text.columns.updatedAt}>
                        {formatDate(
                          operation.lifecycleUpdatedAt ||
                            operation.updatedAt ||
                            operation.createdAt,
                          language
                        )}
                      </td>
                      <td className="account-table-actions">
                        <button
                          type="button"
                          className="account-row-action"
                          aria-label={text.buttons.viewDetails}
                          onClick={() =>
                            openOperationDetails(operation.operation)
                          }
                        >
                          <span>{text.buttons.viewDetails}</span>
                          <i
                            className="fa-solid fa-arrow-up-right-from-square"
                            aria-hidden="true"
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <UserManagementPagination
            page={pagination.currentPage || currentPage}
            totalPages={pagination.totalPages || 1}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            rowsPerPageLabel={text.rowsPerPage}
            pageLabel={text.page}
            previousLabel={text.previous}
            nextLabel={text.next}
            direction={direction}
          />
        </section>
      </main>

      <Offcanvas
        show={showDetails}
        onHide={closeOperationDetails}
        placement={direction === "rtl" ? "start" : "end"}
        className="operations-detail-offcanvas account-drawer"
      >
        <Offcanvas.Header closeButton>
          <div>
            <p className="operations-drawer-eyebrow">
              {text.drawer.eyebrow}
            </p>
            <Offcanvas.Title>{text.detailTitle}</Offcanvas.Title>
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body dir={direction}>
          {detailLoading || !selectedOperation ? (
            <div className="operations-drawer-loading">
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              />
              {text.detailTitle}
            </div>
          ) : (
            <>
              <OperationDrawerHeader
                operation={selectedOperation}
                text={text}
                statusLabels={statusLabels}
              />
              <OperationDrawerTabs
                tabs={drawerTabs}
                activeTab={visibleActiveTab}
                onChange={setActiveTab}
                text={text}
              />
              {visibleActiveTab === "overview" && (
                <OverviewTab
                  operation={selectedOperation}
                  text={text}
                  statusLabels={statusLabels}
                  language={language}
                />
              )}
              {visibleActiveTab === "files" && (
                <ContractFilesTab
                  contract={selectedContract}
                  operation={selectedOperation}
                  contractFileApi={contractFileApi}
                  markReviewedAttempt={markReviewedAttempt}
                  text={text}
                  statusLabels={statusLabels}
                  language={language}
                />
              )}
              {visibleActiveTab === "review" && (
                <ContractReviewDecisionPanel
                  language={language}
                  canReview={
                    getOperationCapabilities({
                      operation: selectedOperation,
                      contract: selectedContract
                    }).canReviewContract
                  }
                  latestAttempt={
                    selectedContract?.latestSignedUploadAttempt ||
                    selectedOperation?.signedUpload
                  }
                  latestDecision={
                    selectedContract?.latestReviewDecision ||
                    selectedOperation?.review
                  }
                  reviewedAttemptId={reviewedAttemptId}
                  checklist={reviewChecklist}
                  checklistLabels={text.checklist}
                  form={reviewForm}
                  onChecklistChange={updateReviewChecklist}
                  onFormChange={setReviewForm}
                  onSubmit={submitReview}
                  actionLoading={actionLoading}
                  reviewError={reviewError}
                />
              )}
              {visibleActiveTab === "fee" && (
                <PlatformFeeTab
                  fee={selectedFee}
                  operation={selectedOperation}
                  manualFeeForm={manualFeeForm}
                  updateManualFeeForm={updateManualFeeForm}
                  submitManualFeeConfirmation={submitManualFeeConfirmation}
                  actionLoading={actionLoading}
                  text={text}
                  statusLabels={statusLabels}
                  language={language}
                />
              )}
              {visibleActiveTab === "activation" && (
                <ActivationTab
                  operation={selectedOperation}
                  text={text}
                />
              )}
              {visibleActiveTab === "events" && (
                <EventsTab
                  events={events}
                  loading={eventsLoading}
                  eventsPagination={eventsPagination}
                  eventPage={eventPage}
                  getEventPageNumbers={getEventPageNumbers}
                  handleEventPageChange={handleEventPageChange}
                  text={text}
                  language={language}
                />
              )}
              {visibleActiveTab === "dispute" && (
                <DisputeTab
                  dispute={selectedDispute}
                  operation={selectedOperation}
                  text={text}
                  statusLabels={statusLabels}
                />
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default AdminInstallmentOperations;
